import { call, put, select, takeEvery } from "redux-saga/effects";

import { getLocaleWithScript } from "@gamedev-web/gamedev-sdk-core";
import { toError } from "@gaming/data-validation";
import { ERT } from "@gaming/extract-redux-saga-return-type";

import { extractUhfContentInTags } from "../helpers";
import { UhfData } from "../models";
import { UhfActions, uhfActions } from "./uhfActions";

const getUhfResponseAsync = async (locale: string): Promise<string> => {
  if (process.env.BUILD_TARGET === "server") {
    const { getUhfResponse } =
      require("../responses/getUhfResponse") as typeof import("../responses/getUhfResponse");
    return getUhfResponse(locale);
  }

  const { getUhfResponse } = await import("../responses/getUhfResponse");
  return getUhfResponse(locale);
};

function* fetchUhf() {
  const locale: string = yield select(getLocaleWithScript);

  const rawData: ERT<typeof getUhfResponseAsync> = yield call(getUhfResponseAsync, locale);
  const cssIncludes = extractUhfContentInTags(rawData, "cssIncludes");
  const javascriptIncludes = extractUhfContentInTags(rawData, "javascriptIncludes");
  const headerHtml = extractUhfContentInTags(rawData, "headerHtml");
  const footerHtml = extractUhfContentInTags(rawData, "footerHtml");

  const uhfData: UhfData = {
    cssIncludes,
    javascriptIncludes,
    headerHtml,
    footerHtml,
  };

  return uhfData;
}

export function* fetchUhfSaga() {
  const startAction = uhfActions.fetchUhf.start();
  yield put(startAction);
  yield call(fetchUhfHandler, startAction);
}

function* fetchUhfHandler(_action: ReturnType<typeof uhfActions.fetchUhf.start>) {
  try {
    const data: ERT<typeof fetchUhf> = yield call(fetchUhf);
    if (data) {
      yield put(uhfActions.fetchUhf.success(data));
    } else {
      throw new Error("Failed to fetch metadata for UHF");
    }
  } catch (e) {
    const error = toError(e);
    console.error(error);
    yield put(uhfActions.fetchUhf.error(error));
  }
}

export function* uhfSagas() {
  yield takeEvery(UhfActions.FETCH_UHF_START, fetchUhfHandler);
}
